import { CSSTransition } from 'react-transition-group';
import {
  ANIMATION_TIMEOUT,
  AvailableFluxImageModels,
  SVG_SIZE_L,
  SVG_SIZE_XL,
} from 'src/constants';
import './ModalWithGallery.scss';
import { MouseEvent, useContext, useMemo, useRef, useState } from 'react';
import SessionContext from 'src/contexts/SessionContext';
import {
  isExternalModelReference,
  isImageGeneratedItem,
  isSearchImageReference,
  isSearchVideoReference,
  isSearchWebPageReference,
  ModalGalleryItemType,
  ModalGalleryType,
  ImageGenRenderData,
  ImageGenerationItem,
  AppRoutes,
  isTextFileItem,
  AiGenerationData,
} from 'src/types';
import { ResearchMediaThumbnail } from 'src/components/FlatAppearance/components/ThreadResearchCard/components/ResearchMediaThumbnail';
import classNames from 'classnames';
import { ViewInBrowserButton } from 'src/components/ModalWithGallery/components/ViewInBrowserButton';
import { LazyReactPlayer } from 'src/components/LazyReactPlayer';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { CloseButton } from 'src/components/ModalWithGallery/components/CloseButton';
import { CaretLeft, CaretRight, Stack, Image } from '@phosphor-icons/react';
import { ExternalModelThumbnail } from 'src/components/FlatAppearance/components/ExternalModelReferencesList/components/ExternalModelThumbnail';
import { ReferenceItem } from 'src/components/ModalWithGallery/components/ReferenceItem';
import { Markdown } from 'src/components/Markdown';
import { ReferenceIframe } from 'src/components/ModalWithGallery/components/ReferenceIframe';
import { CopyButton } from 'src/components/ModalWithGallery/components/CopyButton/CopyButton';
import { useHotkeys } from 'react-hotkeys-hook';
import { Icon } from 'src/components/Icon';
import { useBreakpoint, useSession, useVisible } from 'src/hooks';
import { ProgressBar } from 'src/components/FlatAppearance/components/ThreadCombinedCard/components/ProgressBar';
import { MobileListButton } from 'src/components/ModalWithGallery/components/MobileListButton';
import { MobileModalOverlayList } from 'src/components/ModalWithGallery/components/MobileModalOverlayList';
import { DownloadButton } from 'src/components/ModalWithGallery/components/DownloadButton';
import { useDownloadFile } from 'src/hooks';
import { EditImageButton } from 'src/components/ModalWithGallery/components/EditImageButton';
import { UserMessageContent } from 'src/v2/components/UserMessage/components/UserMessageContent';
import { useNavigate } from 'react-router-dom';
import { UpgradeButton } from '../UpgradeButton';
import cn from 'classnames';
import { AiGenerationStatus } from 'src/types/models/AiGenerationStatus';
import { ImageGenData } from 'src/types/models/ImageGenData';
import { MediaModalError } from 'src/v2/components/MediaModalError';
import { PortalWrapper } from 'src/components/PortalWrapper';

const IMAGE_URL_REGEX =
  /(https?:\/\/.*\.(?:png|jpg|jpeg|apng|bmp|dib|drle|dpx|fits|fit|fts|g3|gif|ico|jp2|jpg2|jfif|pjpeg|pjp|jpm|jpgm|svg|svgz|svg.gz|tif|tiff|tfx|psd|wbmp|xif|pcx|webp|wmf|arw|cr2|crw|dcr|dng|erf|k25|kdc|mrw|nef|nrw|orf|pef|raf|raw|sr2|srf|x3f))/i;

export const ModalWithGallery = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { onDownloadFile } = useDownloadFile();
  const navigate = useNavigate();

  const { isMobileOrTablet, isMobile } = useBreakpoint();
  const {
    isVisible: isOpenMobileListPanel,
    onVisibilityRemove: closeMobileListPanel,
    onVisibilityToggle: toggleIsOpenMobileListPanel,
  } = useVisible();
  const {
    isVisibleModalGallery,
    currentModalGalleryType,
    currentListOfGalleryItems,
    defaultSelectedGalleryIndex,
    onChangeModalGalleryData,
  } = useContext(SessionContext);
  const [selectedItemIndex, setSelectedItemIndex] = useState(
    defaultSelectedGalleryIndex,
  );
  const { isOpenTier, isFreeTier } = useSession();
  const swiperRef = useRef<SwiperRef | null>(null);

  const currentSelectedItem = useMemo(
    () => currentListOfGalleryItems[selectedItemIndex],
    [selectedItemIndex, currentListOfGalleryItems],
  );

  const handleChangeCurrentIndex = (index: number) => {
    if (isMobileOrTablet && isOpenMobileListPanel) {
      closeMobileListPanel();
    }
    setSelectedItemIndex(index);
    swiperRef?.current?.swiper?.slideTo(index);
  };

  useHotkeys('esc', () => {
    onChangeModalGalleryData(false, null, [], 0);
  });

  useHotkeys('left', () => {
    if (selectedItemIndex !== 0) {
      handleChangeCurrentIndex(selectedItemIndex - 1);
    }
  });

  useHotkeys('right', () => {
    if (selectedItemIndex + 1 !== currentListOfGalleryItems.length) {
      handleChangeCurrentIndex(selectedItemIndex + 1);
    }
  });

  useHotkeys('d', () => {
    if (currentModalGalleryType === ModalGalleryType.GENERATED_IMAGES) {
      onDownloadFile(currentSelectedItem.url);
    }
  });

  const handleSlideChange = () => {
    const direction = swiperRef?.current?.swiper.swipeDirection;

    if (direction === 'next') {
      if (selectedItemIndex + 1 !== currentListOfGalleryItems.length) {
        handleChangeCurrentIndex(selectedItemIndex + 1);
      }
    } else if (direction === 'prev') {
      if (selectedItemIndex !== 0) {
        handleChangeCurrentIndex(selectedItemIndex - 1);
      }
    }
  };

  const handleDisabledOverlayClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChangeModalGalleryData(false, null, [], 0);
    if (isOpenTier) {
      navigate(AppRoutes.SIGN_UP);
    }
    if (isFreeTier) {
      navigate(AppRoutes.PAYMENT);
    }
  };

  const renderGalleryItem = (item: ModalGalleryItemType, index: number) => {
    switch (currentModalGalleryType) {
      case ModalGalleryType.TEXT_FILE:
        if (isTextFileItem(item)) {
          return (
            <ReferenceItem
              isActive={index === selectedItemIndex}
              title={`${index + 1}. ${item.title}`}
              onClick={() => handleChangeCurrentIndex(index)}
            />
          );
        }
        return <></>;
      case ModalGalleryType.IMAGES_VIDEOS:
        return (
          <ResearchMediaThumbnail
            thumbnail={isSearchVideoReference(item) ? item.thumbnail : item.url}
            title={item.title || ''}
            isVideo={isSearchVideoReference(item)}
            onClick={() => handleChangeCurrentIndex(index)}
          />
        );
      case ModalGalleryType.EXTERNAL_MODELS:
        if (isExternalModelReference(item)) {
          return (
            <ExternalModelThumbnail
              isActive={index === selectedItemIndex}
              title={item.title}
              icon_name={item.icon_name}
              disabled={!item.content}
              onClick={() => handleChangeCurrentIndex(index)}
            />
          );
        }
        return null;
      case ModalGalleryType.REFERENCES:
        return isSearchWebPageReference(item) ? (
          <ReferenceItem
            isActive={index === selectedItemIndex}
            title={`${index + 1}. ${item.title}`}
            onClick={() => handleChangeCurrentIndex(index)}
          />
        ) : null;
      case ModalGalleryType.GENERATED_IMAGES:
        if (isImageGeneratedItem(item)) {
          return (
            <div className="nj-generated-image-wrapper">
              <div className="nj-generated-image-title-wrapper">
                <h4 className="nj-generated-image-title">
                  {item.icon && <Icon type={item.icon} size={20} />}
                  <span className="text ellipsis">{item.title}</span>
                </h4>
              </div>

              <ResearchMediaThumbnail
                thumbnail={item.url}
                title={item.title}
                status={(item as ImageGenData).status}
                onClick={() => handleChangeCurrentIndex(index)}
                isPixPro={
                  !!(item as ImageGenerationItem).source &&
                  (AvailableFluxImageModels as string[]).includes(
                    (item as ImageGenerationItem).source || '',
                  )
                }
                isDisabled={(item as ImageGenerationItem).isDisabled}
                isGenImage
                withWatermark
              />
            </div>
          );
        }
        return null;
      default:
        return <></>;
    }
  };

  const renderTitle = () => {
    switch (currentModalGalleryType) {
      case ModalGalleryType.TEXT_FILE:
        return (
          <h4 className="nj-modal-gallery-title-text ellipsis">
            {currentSelectedItem.title}
          </h4>
        );
      case ModalGalleryType.IMAGES_VIDEOS:
        return (
          <h4 className="nj-modal-gallery-title-text ellipsis">
            {currentSelectedItem?.title}
          </h4>
        );
      case ModalGalleryType.EXTERNAL_MODELS:
        return (
          <div className="nj-modal-overlay-content-title-wrapper">
            <Stack size={SVG_SIZE_XL} />
            <h4 className="nj-modal-gallery-title-text ellipsis">
              External model results
            </h4>
          </div>
        );
      case ModalGalleryType.REFERENCES:
        return (
          <div className="nj-modal-overlay-content-title-wrapper">
            {isSearchWebPageReference(currentSelectedItem) &&
              currentSelectedItem.favicon && (
                <img
                  src={currentSelectedItem.favicon}
                  alt={currentSelectedItem.title || ''}
                  className="nj-modal-gallery-title-icon"
                />
              )}
            <h4 className="nj-modal-gallery-title-text">
              {currentSelectedItem.title}
            </h4>
          </div>
        );
      case ModalGalleryType.GENERATED_IMAGES:
        return (
          <div className="nj-modal-overlay-content-title-wrapper">
            <Image size={SVG_SIZE_XL} />
            <h4 className="nj-modal-gallery-title-text ellipsis">
              Image generators
            </h4>
          </div>
        );
      default:
        return <></>;
    }
  };

  const renderActivityButtons = () => {
    switch (currentModalGalleryType) {
      case ModalGalleryType.TEXT_FILE:
        return (
          <CopyButton
            text={
              isTextFileItem(currentSelectedItem)
                ? currentSelectedItem.content
                : ''
            }
          />
        );
      case ModalGalleryType.IMAGES_VIDEOS:
        return <ViewInBrowserButton url={currentSelectedItem.url} />;

      case ModalGalleryType.EXTERNAL_MODELS:
        return (
          <>
            <CopyButton
              text={
                isExternalModelReference(currentSelectedItem)
                  ? `${currentSelectedItem.title} \n\n ${currentSelectedItem.content}`
                  : ''
              }
            />
          </>
        );
      case ModalGalleryType.REFERENCES:
        return <ViewInBrowserButton url={currentSelectedItem.url} />;

      case ModalGalleryType.GENERATED_IMAGES:
        const isDisabled = (currentSelectedItem as ImageGenerationItem)
          .isDisabled;
        const withWatermark = (currentSelectedItem as ImageGenerationItem)
          .withWatermark;

        return (
          <>
            {!isMobile && withWatermark && (
              <UpgradeButton
                dataGTM="Image-edit-upgrade"
                title="Upgrade to unlock"
              />
            )}

            {!isMobile &&
              (currentSelectedItem as ImageGenRenderData).status ===
                AiGenerationStatus.SUCCESS &&
              !isOpenTier &&
              !isDisabled && (
                <EditImageButton
                  isDisabled={withWatermark}
                  url={currentSelectedItem.url}
                />
              )}

            {!isOpenTier &&
              !isDisabled &&
              (currentSelectedItem as ImageGenRenderData).status ===
                AiGenerationStatus.SUCCESS && (
                <DownloadButton url={currentSelectedItem.url} />
              )}
          </>
        );
      default:
        return <></>;
    }
  };

  const renderMainItemView = () => {
    switch (currentModalGalleryType) {
      case ModalGalleryType.TEXT_FILE:
        if (isTextFileItem(currentSelectedItem)) {
          return (
            <div
              className="nj-modal-gallery-external-models-wrapper"
              id="printable-area"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="nj-modal-gallery-text-content">
                {currentSelectedItem.content}
              </div>
            </div>
          );
        }
        return <></>;
      case ModalGalleryType.IMAGES_VIDEOS:
        if (isSearchVideoReference(currentSelectedItem)) {
          return (
            <LazyReactPlayer
              url={currentSelectedItem.url}
              width={isMobileOrTablet ? '90%' : '70%'}
              height={isMobileOrTablet ? '60%' : '90%'}
              className="nj-modal-gallery-player nj-modal-gallery-player--video"
              controls
              onClick={(e: MouseEvent) => e.stopPropagation()}
            />
          );
        } else if (isSearchImageReference(currentSelectedItem)) {
          return (
            <img
              src={currentSelectedItem.url}
              alt={currentSelectedItem.title}
              className="nj-modal-gallery-image"
              onClick={(e) => e.stopPropagation()}
            />
          );
        }
        return <></>;
      case ModalGalleryType.EXTERNAL_MODELS:
        return isExternalModelReference(currentSelectedItem) ? (
          <div
            className="nj-modal-gallery-external-models-wrapper"
            id="printable-area"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nj-modal-gallery-external-models-title">
              {currentSelectedItem.icon_name && (
                <Icon size={SVG_SIZE_XL} type={currentSelectedItem.icon_name} />
              )}

              <h4 className="nj-modal-gallery-external-models-title-text">
                {currentSelectedItem.title}
              </h4>
            </div>
            {currentSelectedItem.content ? (
              <div className={isMobileOrTablet ? '' : 'swiper-no-swiping'}>
                {currentSelectedItem.originalQuery && (
                  <h4 className="nj-modal-gallery-external-models-title-text">
                    <UserMessageContent
                      content={currentSelectedItem.originalQuery}
                    />
                  </h4>
                )}

                <Markdown>{currentSelectedItem.content}</Markdown>
              </div>
            ) : (
              <ProgressBar count={3} />
            )}
          </div>
        ) : null;
      case ModalGalleryType.REFERENCES:
        return (
          <ReferenceIframe
            url={currentSelectedItem.url}
            onClick={(e: MouseEvent) => e.stopPropagation()}
          />
        );
      case ModalGalleryType.GENERATED_IMAGES:
        const isDisabled = (currentSelectedItem as ImageGenerationItem)
          .isDisabled;
        const withWatermark = (currentSelectedItem as ImageGenerationItem)
          .withWatermark;
        const showEditImageButton =
          isMobile &&
          IMAGE_URL_REGEX.test(currentSelectedItem.url) &&
          !isOpenTier &&
          !isDisabled;
        const showUpgradeButton = isMobile && withWatermark;

        return (currentSelectedItem as ImageGenRenderData).status ===
          AiGenerationStatus.SUCCESS ? (
          <div
            className={cn('nj-modal-gallery-wrapper-image-gen', {
              withButton: showEditImageButton || showUpgradeButton,
            })}
          >
            <img
              src={currentSelectedItem.url}
              alt={currentSelectedItem.title || ''}
              className={cn('nj-modal-gallery-image-gen', {
                withButton: showEditImageButton || showUpgradeButton,
              })}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="nj-modal-gallery-image-gen--model-label">
              <Icon
                size={SVG_SIZE_L}
                type={
                  (currentSelectedItem as ImageGenRenderData).icon || undefined
                }
              />
              <span>{(currentSelectedItem as ImageGenRenderData).title}</span>
            </div>
            {isDisabled && (
              <button
                type="button"
                className="nj-modal-gallery-wrapper-image-gen-disabled-overlay"
                onClick={handleDisabledOverlayClick}
              >
                <span>Unlock to see</span>
                <div className="nj-thread-research-media--thumbnail-image-disabled-overlay-button">
                  {isOpenTier ? 'Sign up' : 'Upgrade'}
                </div>
              </button>
            )}

            {showEditImageButton && (
              <EditImageButton
                isDisabled={withWatermark}
                url={currentSelectedItem.url}
              />
            )}

            {showUpgradeButton && (
              <UpgradeButton
                dataGTM="Image-edit-upgrade"
                title="Upgrade to unlock"
              />
            )}
          </div>
        ) : (
          <MediaModalError
            status={
              (currentSelectedItem as AiGenerationData).status || undefined
            }
            mediaType="image"
          />
        );
      default:
        return <></>;
    }
  };

  const navigationButtons = (
    <>
      <button
        className="swiper-prev-button"
        title="Press Left"
        disabled={selectedItemIndex === 0}
        onClick={(e) => {
          e.stopPropagation();
          handleChangeCurrentIndex(selectedItemIndex - 1);
        }}
      >
        <CaretLeft size={isMobileOrTablet ? SVG_SIZE_L : SVG_SIZE_XL} />
      </button>
      {isMobileOrTablet && (
        <MobileListButton onClick={toggleIsOpenMobileListPanel} />
      )}
      <button
        className="swiper-next-button"
        title="Press Right"
        disabled={selectedItemIndex + 1 === currentListOfGalleryItems.length}
        onClick={(e) => {
          e.stopPropagation();
          handleChangeCurrentIndex(selectedItemIndex + 1);
        }}
      >
        <CaretRight size={isMobileOrTablet ? SVG_SIZE_L : SVG_SIZE_XL} />
      </button>
    </>
  );

  return (
    <PortalWrapper>
      <CSSTransition
        in={isVisibleModalGallery}
        timeout={ANIMATION_TIMEOUT}
        classNames="nj-animate-fade"
        onEnter={() => {
          setSelectedItemIndex(defaultSelectedGalleryIndex);
          if (swiperRef?.current?.swiper) {
            swiperRef?.current?.swiper?.slideTo(defaultSelectedGalleryIndex);
          }
        }}
        onExited={() => {
          setSelectedItemIndex(0);
        }}
        unmountOnExit
        nodeRef={containerRef}
      >
        <div ref={containerRef} className="nj-modal-overlay">
          <div
            className="nj-modal-overlay-content"
            onClick={() => onChangeModalGalleryData(false, null, [], 0)}
          >
            <div className="nj-modal-overlay-content-header">
              <div
                className="nj-modal-overlay-content-title-wrapper"
                onClick={(e) => e.stopPropagation()}
              >
                {renderTitle()}
              </div>
              <div className="nj-modal-overlay-content-activity-buttons-wrapper">
                {renderActivityButtons()}
                <CloseButton />
              </div>
            </div>
            {currentListOfGalleryItems.length > 0 && (
              <div
                className={classNames('nj-modal-overlay-content-body', {
                  wide:
                    currentModalGalleryType !== ModalGalleryType.IMAGES_VIDEOS,
                })}
              >
                <Swiper
                  centeredSlides={true}
                  spaceBetween={50}
                  slidesPerView={1}
                  initialSlide={selectedItemIndex}
                  direction="horizontal"
                  className="nj-modal-content--carousel-container"
                  ref={swiperRef}
                  onSlideChange={handleSlideChange}
                  allowSlideNext={isMobileOrTablet}
                  allowSlidePrev={isMobileOrTablet}
                >
                  {currentListOfGalleryItems.map((item, i) => (
                    <SwiperSlide key={`${item.url}_${i}`}>
                      {renderMainItemView()}
                    </SwiperSlide>
                  ))}
                  {!isMobileOrTablet && navigationButtons}
                </Swiper>
                {isMobileOrTablet && (
                  <div className="nj-modal-overlay-content-footer">
                    {navigationButtons}
                  </div>
                )}
              </div>
            )}
          </div>
          {!isMobileOrTablet && (
            <div className="nj-modal-overlay-right-side">
              {currentListOfGalleryItems.map((item, i) => (
                <div
                  className={classNames('nj-modal-overlay-list-item-wrapper', {
                    selected: selectedItemIndex === i,
                  })}
                  key={`${item.url}_${i}`}
                >
                  {renderGalleryItem(item, i)}
                </div>
              ))}
            </div>
          )}
          {isMobileOrTablet && (
            <MobileModalOverlayList
              isOpen={isOpenMobileListPanel}
              onClose={closeMobileListPanel}
            >
              <div className="nj-modal-overlay-mobile-list-panel">
                <div
                  className={classNames(
                    'nj-modal-overlay-mobile-list-panel-body',
                    {
                      isGrid:
                        currentModalGalleryType ===
                        ModalGalleryType.IMAGES_VIDEOS,
                      isGeneratedImagesGrid:
                        currentModalGalleryType ===
                        ModalGalleryType.GENERATED_IMAGES,
                    },
                  )}
                >
                  {currentListOfGalleryItems.map((item, i) => (
                    <div
                      className={classNames(
                        'nj-modal-overlay-list-item-wrapper',
                        {
                          selected: selectedItemIndex === i,
                        },
                      )}
                      key={`${item.url}_${i}`}
                    >
                      {renderGalleryItem(item, i)}
                    </div>
                  ))}
                </div>
              </div>
            </MobileModalOverlayList>
          )}
        </div>
      </CSSTransition>
    </PortalWrapper>
  );
};
